import { useNavigate } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { useBag } from "./useBag";
import { formatMoney } from "./api";

export default function BagDrawer({ code, symbol = "KSh" }) {
  const navigate = useNavigate();
  const { items, isOpen, closeBag, setQty, removeItem, subtotal } = useBag();
  const total = subtotal();
  const points = Math.floor(total / 100);

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-50 bg-black/30 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeBag}
      />
      {/* Panel */}
      <aside
        data-testid="bag-drawer"
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-20 px-6 flex items-center justify-between border-b border-zinc-200">
          <span className="text-[11px] uppercase tracking-[0.2em]">Shopping Bag ({items.length})</span>
          <button onClick={closeBag} data-testid="bag-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <p className="font-[Bodoni_Moda] text-2xl">Your bag is empty</p>
              <button
                onClick={closeBag}
                className="mt-6 text-[11px] uppercase tracking-[0.15em] border-b border-black pb-1"
              >
                Continue shopping
              </button>
            </div>
          ) : (
            <ul>
              {items.map((i) => (
                <li key={i.key} className="flex gap-4 px-6 py-5 border-b border-zinc-100" data-testid={`bag-item-${i.key}`}>
                  <div className="w-20 h-28 bg-zinc-100 overflow-hidden shrink-0">
                    {i.image_url && <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-2">
                      <p className="text-sm font-medium">{i.name}</p>
                      <button onClick={() => removeItem(i.key)} className="text-zinc-400 hover:text-black" data-testid={`bag-remove-${i.key}`}>
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    {i.variant_label && (
                      <p className="text-xs text-zinc-500 mt-1 uppercase tracking-[0.1em]">{i.variant_label}</p>
                    )}
                    <div className="mt-auto flex items-center justify-between pt-3">
                      <div className="flex items-center border border-zinc-300">
                        <button className="px-2 py-1" onClick={() => setQty(i.key, i.quantity - 1)} data-testid={`bag-dec-${i.key}`}>
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="px-3 text-sm">{i.quantity}</span>
                        <button className="px-2 py-1" onClick={() => setQty(i.key, i.quantity + 1)} data-testid={`bag-inc-${i.key}`}>
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <span className="text-sm">{formatMoney(i.price * i.quantity, symbol)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-zinc-200 px-6 py-6">
            <div className="flex justify-between text-sm mb-1">
              <span className="uppercase tracking-[0.1em] text-zinc-500">Subtotal</span>
              <span className="font-medium" data-testid="bag-subtotal">{formatMoney(total, symbol)}</span>
            </div>
            <p className="text-xs italic text-zinc-500 mb-5">Earn {points} loyalty points with this order.</p>
            <button
              onClick={() => {
                closeBag();
                navigate(`/store/${code}/checkout`);
              }}
              data-testid="bag-checkout-btn"
              className="w-full h-14 bg-black text-white text-xs uppercase tracking-[0.2em] hover:bg-zinc-800 transition-colors"
            >
              Checkout
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
